import { Link } from 'react-router-dom'
import { Instagram, Linkedin, Mail, Music2, Youtube } from 'lucide-react'
import { Logo } from '@/shared/ui/logo'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'
import { NewsletterForm } from '@/features/newsletter/newsletter-form'
import {
  getHiddenPaths,
  usePageStatusStore,
} from '@/features/page-status/store'
import { useCurrentUser } from '@/features/auth/store'
import type { IconComponent } from '@/shared/types/icon'

const T = defineT({
  tagline: {
    ar: 'مصمم ومطور ومتخصص في الذكاء الاصطناعي. بنشرّك من الصفر للاحتراف.',
    en: 'Designer, developer, and AI specialist. From zero to pro — together.',
  },
  explore: { ar: 'تصفّح', en: 'Explore' },
  learn: { ar: 'تعلّم', en: 'Learn' },
  resources: { ar: 'مكتبة', en: 'Resources' },
  contact: { ar: 'تواصل', en: 'Contact' },
  rights: { ar: 'كل الحقوق محفوظة', en: 'All rights reserved' },
  // Explore
  home: { ar: 'الرئيسية', en: 'Home' },
  about: { ar: 'من أنا', en: 'About' },
  blog: { ar: 'المقالات', en: 'Blog' },
  libraryLink: { ar: 'مكتبتي', en: 'My Library' },
  aiNews: { ar: 'أخبار الـ AI', en: 'AI News' },
  // Learn
  courses: { ar: 'الدورات', en: 'Courses' },
  tutorials: { ar: 'شروحات مجانية', en: 'Tutorials' },
  aiCourse: { ar: 'كورس AI', en: 'AI Course' },
  // Resources
  prompts: { ar: 'البرومبتات', en: 'Prompts' },
  toolshub: { ar: 'دليل الأدوات', en: 'Tools Hub' },
  myTools: { ar: 'أدواتي', en: 'My Tools' },
  zatoona: { ar: 'الزتونة', en: 'Zatoona' },
  promptEngineering: { ar: 'هندسة الأوامر', en: 'Prompt Engineering' },
  glossary: { ar: 'قاموس الـ AI', en: 'AI Glossary' },
  designSystems: { ar: 'أنظمة التصميم', en: 'Design Systems' },
  vibeCoding: { ar: 'Vibe Coding', en: 'Vibe Coding' },
  freeApis: { ar: 'APIs مجانية', en: 'Free APIs' },
  // Legal
  legal: { ar: 'قانوني', en: 'Legal' },
  privacy: { ar: 'سياسة الخصوصية', en: 'Privacy Policy' },
  terms: { ar: 'شروط الاستخدام', en: 'Terms of Service' },
  contactLink: { ar: 'تواصل معايا', en: 'Contact me' },
  // Newsletter
  newsletterTitle: { ar: 'اشترك في النشرة', en: 'Subscribe' },
  newsletterBody: {
    ar: 'أحدث الكورسات والأدوات في إيميلك أول بأول.',
    en: 'New courses and tools, straight to your inbox.',
  },
  // Contact
  email: { ar: 'البريد الإلكتروني', en: 'Email' },
  social: { ar: 'تابعني', en: 'Follow' },
})

export function Footer() {
  const { t } = useTranslation(T)
  const year = new Date().getFullYear()

  // Hide links to pages flagged as `hidden` in the page-status table.
  // Admins see everything so they can preview hidden pages directly.
  const pageStatusList = usePageStatusStore((s) => s.list)
  const currentUser = useCurrentUser()
  const hiddenPaths =
    currentUser?.role === 'admin' ? new Set<string>() : getHiddenPaths(pageStatusList)

  const cols: Array<{ title: keyof typeof T; links: Array<{ to: string; key: keyof typeof T }> }> = [
    {
      title: 'explore',
      links: [
        { to: '/', key: 'home' },
        { to: '/about', key: 'about' },
        { to: '/blog', key: 'blog' },
        { to: '/ai-news', key: 'aiNews' },
        { to: '/library', key: 'libraryLink' },
      ],
    },
    {
      title: 'learn',
      links: [
        { to: '/courses', key: 'courses' },
        { to: '/tutorials', key: 'tutorials' },
        { to: '/ai-course', key: 'aiCourse' },
      ],
    },
    {
      title: 'resources',
      links: [
        { to: '/prompts', key: 'prompts' },
        { to: '/toolshub', key: 'toolshub' },
        { to: '/freetools', key: 'myTools' },
        { to: '/zatoona', key: 'zatoona' },
        { to: '/prompt-engineering', key: 'promptEngineering' },
        { to: '/design-systems', key: 'designSystems' },
        { to: '/vibe-coding', key: 'vibeCoding' },
        { to: '/free-apis', key: 'freeApis' },
        { to: '/glossary', key: 'glossary' },
      ],
    },
    {
      title: 'legal',
      links: [
        { to: '/contact', key: 'contactLink' },
        { to: '/privacy', key: 'privacy' },
        { to: '/terms', key: 'terms' },
      ],
    },
  ]

  return (
    <footer className="relative mt-16 border-t border-border bg-card/40">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Newsletter rail (above the columns on every footer instance) */}
        <div className="mb-14 grid gap-6 rounded-3xl border border-primary/20 bg-gradient-to-br from-card via-card to-primary/5 p-6 sm:grid-cols-12 sm:items-center sm:p-8">
          <div className="sm:col-span-5">
            <p className="font-display text-xl font-bold tracking-tight">
              {t('newsletterTitle')}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {t('newsletterBody')}
            </p>
          </div>
          <div className="sm:col-span-7">
            <NewsletterForm />
          </div>
        </div>

        <div className="grid gap-12 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Logo size={44} showWordmark />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-muted-foreground">
              {t('tagline')}
            </p>
            <div className="mt-6 flex items-center gap-2">
              <SocialLink href="mailto:hello@abdallah-issa.com" icon={Mail} label="Email" />
              <SocialLink href="https://www.youtube.com/@abdallahissa" icon={Youtube} label="YouTube" />
              <SocialLink href="https://www.instagram.com/abdallahissa" icon={Instagram} label="Instagram" />
              <SocialLink href="https://www.linkedin.com/in/abdallahissa/" icon={Linkedin} label="LinkedIn" />
              <SocialLink href="https://www.tiktok.com/@abdallahissa120" icon={Music2} label="TikTok" />
            </div>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4 lg:col-span-8">
            {cols.map((col) => {
              const visibleLinks = col.links.filter(
                (l) => !hiddenPaths.has(l.to),
              )
              if (visibleLinks.length === 0) return null
              return (
                <div key={col.title}>
                  <h4 className="mb-4 text-sm font-semibold tracking-wide text-foreground">
                    {t(col.title)}
                  </h4>
                  <ul className="space-y-2.5">
                    {visibleLinks.map((link) => (
                      <li key={link.key}>
                        <Link
                          to={link.to}
                          className="text-sm text-muted-foreground transition-colors hover:text-primary"
                        >
                          {t(link.key)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-border pt-6 sm:flex-row sm:items-center">
          <p className="text-xs text-muted-foreground">
            © {year} Abdallah Issa. {t('rights')}.
          </p>
        </div>
      </div>
    </footer>
  )
}

function SocialLink({
  href,
  icon: Icon,
  label,
}: {
  href: string
  icon: IconComponent
  label: string
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={label}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card/60 text-foreground/70 transition-colors hover:border-primary/50 hover:text-primary"
    >
      <Icon className="h-4 w-4" aria-hidden />
    </a>
  )
}
